// @flow
import { useRef, useState, useLayoutEffect, type Node } from 'react';
import { animated, useSpring } from 'react-spring/web.cjs';
import { color, DEPTH, SPACING } from '@tablekit/theme';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Z_INDICES } from '../../utils/theme';
import { MenuItem, MenuHeader } from './styles';

const MIN_MENU_ITEM_WIDTH = 220;
const MIN_ITEMS_ONE_COLUMN = 4;

const DropMenuGrid = styled.div`
  ${({ itemCount, cellWidth }) => {
    const correctedWidth = Math.max(cellWidth, MIN_MENU_ITEM_WIDTH);
    if (itemCount <= MIN_ITEMS_ONE_COLUMN) {
      return css`
        display: flex;
        flex-direction: column;
        min-width: ${correctedWidth}px;
      `;
    }
    return css`
      display: -ms-grid;
      display: grid;
      // prettier-ignore
      -ms-grid-columns: (${correctedWidth}px)[2];
      grid-template-columns: repeat(2, ${correctedWidth}px);
      grid-template-rows: repeat(${Math.ceil(itemCount / 2)}, 1fr);
      grid-auto-flow: column;
    `;
  }}
`;

const DropMenu = animated(styled.div`
  position: absolute;
  top: calc(100% + ${SPACING.L3});
  color: ${color('text')};
  z-index: ${Z_INDICES.NAV};
  background-color: ${color('surface')};
  ${DEPTH.FLOAT};
  transform-origin: top center;

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: -${SPACING.L2};
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
  }
`);

export const DropMenuItem = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  }};
`;

const preventTouchStartOnRef = (ref) => {
  if (!ref) return;
  // see https://stackoverflow.com/questions/55461449/unable-to-preventdefault-inside-passive-event-listener-due-to-target-being-treat
  // weird chrome thing
  ref.addEventListener(
    'touchstart',
    (event: SyntheticEvent<HTMLAnchorElement>) => {
      // this allows us to use "touch" events to toggle menus on mobile
      event.preventDefault();
    },
    { passive: false }
  );
};

export default function DesktopDropMenu({
  isOpen,
  createToggleOpen,
  id,
  header,
  items,
  itemsRenderer
}: {
  createToggleOpen: (string) => () => void,
  header: Node,
  id: string,
  isOpen: boolean,
  items: any[],
  itemsRenderer: (item: any, index: number, ref: any) => Node
}) {
  const toggleMenuOpen = createToggleOpen(id);
  const toggleMenuClosed = createToggleOpen('');
  const [width, setWidth] = useState(MIN_MENU_ITEM_WIDTH);
  // heavy use of Refs instead of state to avoid uneccessary re-renders due to resizing/animations
  const widthCollectionRef = useRef(MIN_MENU_ITEM_WIDTH);
  const isLeftSideRef = useRef(true);
  const updateIsLeftSide = (target: any) => {
    if (!target || !target.parentNode || !target.parentNode.parentNode) return;
    isLeftSideRef.current =
      target.offsetLeft +
        target.parentNode.offsetLeft +
        target.parentNode.parentNode.offsetLeft <
      target.parentNode.parentNode.parentNode.parentNode.offsetWidth / 2;
  };
  const { transform, opacity } = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: `perspective(2000px) rotateX(${
      isOpen ? 0 : -20
    }deg) translateY(${isOpen ? 0 : -4}px)`,
    config: !isOpen
      ? { duration: 150 }
      : { mass: 5, tension: 500, friction: 80 }
  });

  useLayoutEffect(() => {
    if (width === Math.ceil(widthCollectionRef.current)) return;
    setWidth(Math.ceil(widthCollectionRef.current));
  });

  return (
    <MenuItem
      id={id}
      onMouseEnter={toggleMenuOpen}
      className={isOpen ? 'open' : 'closed'}
    >
      <MenuHeader
        key={id}
        onMouseEnter={(event: SyntheticMouseEvent<>) => {
          const target = event.currentTarget;
          updateIsLeftSide(target);
          toggleMenuOpen();
        }}
        ref={preventTouchStartOnRef}
        onTouchEnd={(event: SyntheticTouchEvent<>) => {
          const target = event.currentTarget;
          updateIsLeftSide(target);
          if (isOpen) toggleMenuClosed();
          else toggleMenuOpen();
        }}
      >
        {header}
      </MenuHeader>
      <DropMenu
        style={{
          left: isLeftSideRef.current ? 0 : undefined,
          right: isLeftSideRef.current ? undefined : 0,
          width:
            (items.length > MIN_ITEMS_ONE_COLUMN ? 2 : 1) *
            Math.max(width, MIN_MENU_ITEM_WIDTH),
          opacity,
          transform,
          display: opacity.interpolate((v) => (v === 0 ? 'none' : 'block'))
        }}
      >
        <DropMenuGrid itemCount={items.length} cellWidth={width}>
          {items.map((item, index) =>
            itemsRenderer(item, index, (ref) => {
              // due to Flex reverting to display block when inside a position: absolute
              // element we need this code to make sure both columns are the same width
              // also offsetWidth is 0 when display: none so need to clone and position
              if (!ref) return;
              const clone = ref.cloneNode(true);
              const wrappedClone = document.createElement('div');
              wrappedClone.style.position = 'absolute';
              wrappedClone.style.top = '-99999999px';
              wrappedClone.style.right = '-99999999px';
              wrappedClone.style.display = 'block';
              wrappedClone.appendChild(clone);
              // $FlowFixMe
              document.body.appendChild(wrappedClone);
              const realWidth = clone.offsetWidth;
              widthCollectionRef.current = Math.max(
                realWidth,
                widthCollectionRef.current
              );
              // $FlowFixMe
              document.body.removeChild(wrappedClone);
            })
          )}
        </DropMenuGrid>
      </DropMenu>
    </MenuItem>
  );
}
